import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Col, Grid, Row } from 'rsuite';
import { convertNumberToCurrency } from 'src/common/utils';
import { PAYMENT_METHODS, FLOWS_ENUM } from 'src/common/enums';
import { PaymentMethod, PostbackStatus } from 'src/types/payment';
import { SnapPayGetIframeResponse } from './types';
import Notice from '../../user-feedback/Notice';
import { buildPaymentApi } from '../../../api';
import { getPaymentCategory } from '../../../common/paymentUtils';
import { resolvePaymentErrorMessage } from '../../../api/payment/paymentErrors';
import { selectPciPaymentApiEnabled } from '../../../store/slices/config/configSelectors';
import { selectCurrentIDToken } from '../../../store/slices/auth/authSelectors';
import { GENERIC_PAYMENT_ERROR } from '../constants';

interface SnapPayProps {
  flow: FLOWS_ENUM;
  paymentID: string;
  paymentMethod: PaymentMethod;
  signature: string;
  signatoryName: string;
  userEmail: string;
  totalDue: number;
  credits: number;
  onSuccess: (isGeneric?: boolean) => void;
}

const SNAPPAY_IFRAME_ELEMENT_IDS = [
  'interop_form',
  'interopform_overlay',
  'interopform_iframe',
  'receipt_overlay',
  'receipt_iframe',
];

export const SnapPay = ({
  flow,
  paymentID,
  paymentMethod,
  signature,
  signatoryName,
  userEmail,
  totalDue,
  credits,
  onSuccess,
}: SnapPayProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const pciEnabled = useSelector(selectPciPaymentApiEnabled);
  const authToken = useSelector(selectCurrentIDToken);
  const api = useMemo(() => buildPaymentApi(pciEnabled), [pciEnabled]);

  const paymentMode =
    paymentMethod === PAYMENT_METHODS.ACH_DIRECT_DEBIT ? 'ACH' : 'CC';

  const cleanup = useCallback(() => {
    SNAPPAY_IFRAME_ELEMENT_IDS.forEach((id) => {
      document.getElementById(id)?.remove();
    });
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const handlePay = useCallback(async () => {
    setLoading(true);
    setError(undefined);

    try {
      const genericResponse = await api.payGeneric(
        {
          payment_id: Number(paymentID),
          signatory: signatoryName,
          email: userEmail,
          subtotal: totalDue,
          payment_category: getPaymentCategory(flow)!,
          signature,
          credits,
          payload: { paymentMode },
        },
        authToken
      );
      const { interopReqNo, interopJSURL, interopMainURL } =
        genericResponse.formData as unknown as SnapPayGetIframeResponse;

      const messageHandler = async (event: MessageEvent) => {
        if (event.data?.hppurl) {
          // User dismissed the FiServ overlay — reset to the initial state.
          cleanup();
          setLoading(false);
          return;
        }

        // FiServ overlay has fired — post the token now so GetPaymentDetails returns the real result.
        window.removeEventListener('message', messageHandler);

        try {
          const postbackResult = await api.snapPayPostback(
            paymentID,
            interopReqNo,
            paymentMode
          );
          cleanup();
          if (
            postbackResult.status === PostbackStatus.COMPLETE ||
            postbackResult.status === PostbackStatus.PENDING
          ) {
            onSuccess(true);
          } else {
            setError(GENERIC_PAYMENT_ERROR);
            setLoading(false);
          }
        } catch (err) {
          cleanup();
          setError(resolvePaymentErrorMessage(err, GENERIC_PAYMENT_ERROR));
          setLoading(false);
        }
      };

      window.addEventListener('message', messageHandler);

      const script = document.createElement('script');
      script.id = 'interop_form';
      script.src = interopJSURL;
      script.dataset.requestid = interopReqNo;
      script.dataset.interopurl = interopMainURL;
      document.body.appendChild(script);
    } catch (err) {
      cleanup();
      setError(resolvePaymentErrorMessage(err, GENERIC_PAYMENT_ERROR));
      setLoading(false);
    }
  }, [
    flow,
    paymentID,
    signature,
    signatoryName,
    userEmail,
    totalDue,
    credits,
    paymentMode,
    authToken,
    api,
    cleanup,
    onSuccess,
  ]);

  return (
    <Grid
      fluid
      data-testid="snappay-form-component"
    >
      <Row className="mb-16">
        <Col xs={24}>
          <p>
            The button below will open a secure payment window to complete your
            payment.
          </p>
        </Col>
      </Row>
      {error && (
        <Row className="mb-16">
          <Col xs={24}>
            <Notice message={error} />
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={24}>
          <Button
            className="btn-primary full-width"
            disabled={loading}
            onClick={handlePay}
          >
            Pay {convertNumberToCurrency(totalDue)}
          </Button>
        </Col>
      </Row>
    </Grid>
  );
};
